<script>
import MainLayout from '@layouts/main.vue'
import {
  authComputed,
  userMethods,
  userComputed,
  cityComputed,
  cityMethods,
  groupComputed,
  groupMethods,
  usersFiltersMethods,
  usersFiltersComputed,
} from '@state/helpers'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'

export default {
  page: {
    title: 'Savanoriai',
  },

  components: { MainLayout },

  data() {
    return {
      form: {
        keyword: '',
        city: '',
        sort: 'first_name',
        role: '',
        group: '',
        edition: '',
        active: true,
        inactive: false,
        page: 1,
        perPage: 25,
        nv_program: false,
        kc_program: false,
        drauge_program: false,
        grupese_program: false,
      },
      dialogs: {
        filters: {
          visible: false,
        },
      },
    }
  },

  computed: {
    ...authComputed,

    ...userComputed,

    ...cityComputed,

    ...groupComputed,

    ...usersFiltersComputed,

    monthNames() {
      return [
        'Sausio',
        'Vasario',
        'Kovo',
        'Balandžio',
        'Gegužės',
        'Birželio',
        'Liepos',
        'Rugpjūčio',
        'Rugsėjo',
        'Spalio',
        'Lapkričio',
        'Gruodžio',
      ]
    },

    roleOptions() {
      return [
        {
          id: 'volunteer',
          label: 'Savanoriai',
        },
        {
          id: 'coordinator',
          label: 'Koordinatoriai',
        },
        {
          id: 'admin',
          label: 'Administratoriai',
        },
      ]
    },
  },

  created() {
    this.setFilters()
    this.fetchData()
    this.fetchAllCities()
    this.fetchAllGroups()
  },

  methods: {
    ...userMethods,

    ...cityMethods,

    ...groupMethods,

    ...usersFiltersMethods,

    setFilters() {
      Object.keys(this.usersFilters).forEach((key) => {
        const val = this.usersFilters[key]
        this.form[key] = val
      })

      if (this.currentUser.role !== 'admin') {
        this.form.city = this.currentUser.city
      }
    },

    usersFiltersParams() {
      const params = { ...this.usersFilters }
      if (!params.nv_program) delete params.nv_program
      if (!params.kc_program) delete params.kc_program
      if (!params.drauge_program) delete params.drauge_program
      if (!params.grupese_program) delete params.grupese_program
      return params
    },

    fetchData() {
      this.$Progress.start()
      this.fetchAllUsers(this.usersFiltersParams())
        .then(() => this.$Progress.finish())
        .catch(() => this.$Progress.fail())
    },

    onChangeSearchKeyword: debounce(function () {
      this.setUsersFilters({ ...this.form, page: 1 })
      this.fetchData()
    }, 500),

    onSubmitFilter() {
      this.dialogs.filters.visible = false
      this.page = 1

      this.setUsersFilters(this.form)
      this.fetchData()
    },

    changeSort(field) {
      if (field === this.form.sort) {
        if (field[0] === '-') {
          this.form.sort = field.replace('-', '')
        } else {
          this.form.sort = `-${field}`
        }
      } else {
        this.form.sort = field
      }

      this.setUsersFilters(this.form)
      this.fetchData()
    },

    changePage(page) {
      this.form.page = page

      this.setUsersFilters(this.form)
      this.fetchData()
    },

    displayDate(date) {
      const year = dayjs(date).format('YYYY')
      const month = dayjs(date).format('M')
      const day = dayjs(date).format('D')

      return `${year} ${this.monthNames[month - 1]} ${day} d.`
    },
  },
}
</script>

<template>
  <MainLayout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold"> Savanoriai </h1>
        <div
          v-if="usersPagination"
          class="text-sm text-gray-600 font-medium -mt-2"
        >
          {{ usersPagination.totalItems | countDisplay }}
        </div>
      </template>
      <template v-slot:actions>
        <div>
          <div class="flex mb-4">
            <base-input
              v-model="form.keyword"
              type="text"
              data-cypress-keyword
              placeholder="Paieškos raktažodis..."
              class="w-full input"
              :class="{
                rounded: currentUser.role === 'volunteer',
                'rounded-l': currentUser.role !== 'volunteer',
              }"
              autofocus
              @change="onChangeSearchKeyword"
            />
            <button
              v-if="currentUser.role !== 'volunteer'"
              type="button"
              class="w-12 rounded-r bg-gray-300 text-center cursor-pointer btn-main"
              data-cypress-filter
              @click="dialogs.filters.visible = true"
            >
              <font-awesome-icon :icon="['fas', 'bars']" />
            </button>
          </div>
        </div>
        <div v-if="currentUser.role !== 'volunteer'" class="lg:ml-4 mb-4">
          <base-btn @click="$router.push({ name: 'user-create' })">
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
            Sukurti profilį
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <div class="-mx-4 lg:-mx-8">
      <div v-if="currentUser.role !== 'volunteer'" class="overflow-x-auto">
        <table v-if="users && users.length" class="table-auto w-full">
          <thead>
            <tr class="text-left border-t border-b">
              <th></th>
              <th
                class="cursor-pointer text-left"
                data-cypress-sort
                @click="changeSort('first_name')"
              >
                Vardas, pavardė
                <base-sort-icon
                  :value="form.sort"
                  field="first_name"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-first_name"
                  direction="down"
                />
              </th>
              <th
                class="cursor-pointer text-left"
                data-cypress-sort
                @click="changeSort('group')"
              >
                Grupė
                <base-sort-icon
                  :value="form.sort"
                  field="group"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-group"
                  direction="down"
                />
              </th>
              <th>Būsena</th>
              <th
                v-if="
                  currentUser.role === 'admin' ||
                  currentUser.role === 'coordinator'
                "
              >
                Valandos
              </th>
              <th class="cursor-pointer" @click="changeSort('city')">
                Miestas
                <base-sort-icon
                  :value="form.sort"
                  field="city"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-city"
                  direction="down"
                />
              </th>
              <th class="cursor-pointer" @click="changeSort('contract_to')">
                Sutartis galioja iki
                <base-sort-icon
                  :value="form.sort"
                  field="contract_to"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-contract_to"
                  direction="down"
                />
              </th>
              <th
                class="cursor-pointer"
                @click="changeSort('health_certificate_to')"
              >
                Pažyma galioja iki
                <base-sort-icon
                  :value="form.sort"
                  field="health_certificate_to"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-health_certificate_to"
                  direction="down"
                />
              </th>
              <th>Komentaras</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in users"
              :key="user.id"
              :class="{
                'bg-red-200': user.show_certificate_expiration_notice,
                'bg-orange-200':
                  !user.show_certificate_expiration_notice &&
                  user.show_health_check_expiration_notice,
              }"
            >
              <td class="w-24">
                <base-avatar
                  :filename="user.avatar_thumb"
                  class="w-16 h-16 border border-white mr-2"
                />
              </td>
              <td>
                <router-link
                  :to="{ name: 'user-edit', params: { id: user.id } }"
                  class="font-semibold"
                >
                  {{ user.first_name }} {{ user.last_name }}
                </router-link>
                <div class="text-xs text-gray-600 flex items-center">
                  <font-awesome-icon :icon="['fas', 'envelope']" class="mr-1" />
                  <a :href="`mailto:${user.email}`" class="text-gray-600">{{
                    user.email
                  }}</a>
                </div>
                <div class="text-xs text-gray-600 flex items-center">
                  <font-awesome-icon :icon="['fas', 'phone']" class="mr-1" />
                  {{ user.phone }}
                </div>
              </td>
              <td>
                <span
                  v-if="user.group"
                  class="rounded bg-gray-100 py-1 px-2 text-xs"
                >
                  {{ user.group }}
                </span>
              </td>
              <td>
                {{ user.status | statusDisplay }}
              </td>
              <td
                v-if="
                  currentUser.role === 'admin' ||
                  currentUser.role === 'coordinator'
                "
                :title="`Nuo sutarties pradžios budėta ${user.contract_hours} val., o iš viso ${user.total_hours} val.`"
              >
                {{ user.contract_hours }} / {{ user.total_hours }}
              </td>
              <td>
                {{ user.city }}
              </td>
              <td>
                <template v-if="user.contract_to">
                  {{ displayDate(user.contract_to) }}
                </template>
              </td>
              <td>
                <template v-if="user.health_certificate_to">
                  {{ displayDate(user.health_certificate_to) }}
                </template>
              </td>
              <td>
                {{ user.notes }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="overflow-x-auto">
        <table v-if="users && users.length" class="table-auto w-full">
          <thead>
            <tr class="text-left border-t border-b">
              <th></th>
              <th
                class="cursor-pointer text-left"
                data-cypress-sort
                @click="changeSort('group')"
              >
                Grupė
                <base-sort-icon
                  :value="form.sort"
                  field="group"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-group"
                  direction="down"
                />
              </th>
              <th
                class="cursor-pointer text-left"
                data-cypress-sort
                @click="changeSort('first_name')"
              >
                Vardas, pavardė
                <base-sort-icon
                  :value="form.sort"
                  field="first_name"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-first_name"
                  direction="down"
                />
              </th>
              <th class="cursor-pointer" @click="changeSort('email')">
                El. paštas
                <base-sort-icon
                  :value="form.sort"
                  field="email"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-email"
                  direction="down"
                />
              </th>
              <th class="cursor-pointer" @click="changeSort('phone')">
                Tel. Nr.
                <base-sort-icon
                  :value="form.sort"
                  field="phone"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-phone"
                  direction="down"
                />
              </th>
              <th>Būsena</th>
              <th class="cursor-pointer" @click="changeSort('city')">
                Miestas
                <base-sort-icon
                  :value="form.sort"
                  field="city"
                  direction="up"
                />
                <base-sort-icon
                  :value="form.sort"
                  field="-city"
                  direction="down"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in users"
              :key="user.id"
              :class="{
                'bg-red-200': user.show_certificate_expiration_notice,
                'bg-orange-200':
                  !user.show_certificate_expiration_notice &&
                  user.show_health_check_expiration_notice,
              }"
            >
              <td class="w-24">
                <base-avatar
                  :filename="user.avatar_thumb"
                  class="w-10 h-10 mx-auto border border-white"
                />
              </td>
              <td>
                <span
                  v-if="user.group"
                  class="rounded bg-gray-100 py-1 px-2 text-xs"
                >
                  {{ user.group }}
                </span>
              </td>
              <td>{{ user.first_name }} {{ user.last_name }}</td>
              <td>
                <a :href="`mailto:${user.email}`">{{ user.email }}</a>
              </td>
              <td>
                {{ user.phone }}
              </td>
              <td>
                {{ user.status | statusDisplay }}
              </td>
              <td>
                {{ user.city }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <base-pagination :pagination="usersPagination" @change="changePage" />

    <base-dialog
      v-if="dialogs.filters.visible"
      title="Paieškos filtras"
      overlay-closes
      @close="dialogs.filters.visible = false"
    >
      <div v-if="currentUser.role === 'admin'" class="mb-4">
        <base-select
          v-model="form.city"
          :options="cityOptions || []"
          type="text"
          :blank-option="{ id: '', label: 'Miestas...' }"
        />
      </div>

      <div class="mb-4">
        <base-select
          v-model="form.group"
          :options="groupOptions"
          type="text"
          :blank-option="{ id: '', label: 'Grupė...' }"
        />
      </div>

      <div class="mb-4">
        <base-input
          v-model="form.edition"
          type="text"
          class="w-full input"
          placeholder="Laida..."
        />
      </div>

      <div class="mb-4">
        <base-select
          v-model="form.role"
          :options="roleOptions"
          type="text"
          :blank-option="{ id: '', label: 'Statusas...' }"
        />
      </div>

      <div class="flex flex-col lg:flex-row mb-4">
        <div class="mr-4">
          <base-label-check v-model="form.nv_program" type="text" label="NV" />
        </div>
        <div class="mr-4">
          <base-label-check v-model="form.kc_program" type="text" label="KC" />
        </div>
        <div class="mr-4">
          <base-label-check
            v-model="form.drauge_program"
            type="text"
            label="Drauge"
          />
        </div>
        <div class="mr-4">
          <base-label-check
            v-model="form.grupese_program"
            type="text"
            label="Grupėse"
          />
        </div>
      </div>

      <div class="flex flex-col lg:flex-row mb-4">
        <div class="mr-4">
          <base-label-check
            v-model="form.active"
            type="text"
            label="Rodyti dirbančius"
          />
        </div>
        <div>
          <base-label-check
            v-model="form.inactive"
            type="text"
            label="Rodyti nedirbančius"
          />
        </div>
      </div>

      <template v-slot:buttons>
        <div class="w-32 ml-4">
          <base-btn class="p-3" @click="onSubmitFilter"> Filtruoti </base-btn>
        </div>
      </template>
    </base-dialog>
  </MainLayout>
</template>
